import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { T, NUMERIC } from '../customer/customers.theme';
import { CalendarIcon, ChevronIcon } from '../customer/CustomersUI';
import DateRangePopover from '../orders/DateRangePopover';
import { money, percent } from '../expenses/expenses.summary';
import {
  fetchIncomeStatement,
  isEmptyStatement,
  normaliseStatement,
} from './reportsApi';

/**
 * The income statement.
 *
 * A port of the phone's `income_statement_screen.dart`, block for block: a
 * summary card carrying the date control, the net income and the two figures
 * that produce it; then Revenue with its category breakdown and a footer
 * reconciling gross and discounts to the net above; then Qarashaad with its
 * own breakdown.
 *
 * The wording is the phone's, including the fact that the expenses heading is
 * in Somali and the revenue heading is not — that is what the shop reads, and
 * the two clients must not word the same report differently.
 *
 * Nothing on this screen is computed from rows. Every figure is the server's,
 * so the till can never be a second opinion on the shop's profit.
 */

const FMT = 'YYYY-MM-DD';

const COPY = {
  netIncome: 'Net income',
  revenue: 'Revenue',
  expenses: 'Qarashaad',

  gross: 'Total:',
  discounts: 'Discounts:',

  emptyTitle: 'Nothing to report',
  emptyBody: 'No revenue or expenses in this date range.',

  errorTitle: 'Could not load the report',
  retry: 'Try again',

  restricted: 'Only managers can view this page.',

  orders: (n) => (n === 1 ? '1 order' : `${n} orders`),
  entries: (n) => (n === 1 ? '1 entry' : `${n} entries`),
};

/** Manager or accountant — the roles `restrictTo` lets past the route. */
const ALLOWED_ROLES = ['manager', 'accountant', 'superadmin-superadmin'];

/** Month to date, the period an income statement is normally read for. */
const monthToDate = () => ({
  start: moment().startOf('month').format(FMT),
  end: moment().format(FMT),
});

export default function IncomeStatement() {
  const activeUser = useSelector((state) => state.login.activeUser);
  const { business } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const [range, setRange] = React.useState(monthToDate);
  const [statement, setStatement] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [datesOpen, setDatesOpen] = React.useState(false);
  /** Bumped to refetch the same period after a failure. */
  const [attempt, setAttempt] = React.useState(0);

  const dateAnchor = React.useRef(null);

  const allowed = ALLOWED_ROLES.indexOf(activeUser?.role) !== -1;

  React.useEffect(() => {
    if (!allowed || !business?._id) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');

    fetchIncomeStatement({
      businessId: business._id,
      start: range.start,
      end: range.end,
      token,
    })
      .then((raw) => {
        if (cancelled) return;
        setStatement(normaliseStatement(raw));
      })
      .catch((err) => {
        if (cancelled) return;
        setStatement(null);
        setError(err?.response?.data?.message || COPY.errorTitle);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed, business, token, range, attempt]);

  const rangeLabel = React.useMemo(
    () =>
      `${moment(range.start, FMT).format('D MMM YYYY')} — ${moment(range.end, FMT).format(
        'D MMM YYYY'
      )}`,
    [range]
  );

  if (!allowed) {
    return <MessageBlock title={COPY.restricted} />;
  }

  const empty = !loading && !error && isEmptyStatement(statement);

  return (
    <div style={{ maxWidth: 940, display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* ── the headline, which carries the way out of every other state ── */}
      <SummaryCard
        rangeLabel={rangeLabel}
        statement={statement}
        anchorRef={dateAnchor}
        onPickDate={() => setDatesOpen((open) => !open)}
      >
        {datesOpen ? (
          <DateRangePopover
            range={range}
            anchorRef={dateAnchor}
            onClose={() => setDatesOpen(false)}
            onApply={(next) => {
              setRange(next);
              setDatesOpen(false);
            }}
          />
        ) : null}
      </SummaryCard>

      {error ? (
        <MessageBlock title={COPY.errorTitle} body={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : empty ? (
        <MessageBlock title={COPY.emptyTitle} body={COPY.emptyBody} />
      ) : (
        <React.Fragment>
          <SectionHeader
            label={COPY.revenue}
            trailing={statement ? COPY.orders(statement.revenue.orderCount) : null}
          />
          <CategoryCard
            lines={statement ? statement.revenue.categories : []}
            loading={loading}
            rows={3}
            footer={
              statement ? (
                <RevenueFooter
                  gross={statement.revenue.gross}
                  discounts={statement.revenue.discounts}
                />
              ) : null
            }
          />

          <SectionHeader
            label={COPY.expenses}
            trailing={statement ? COPY.entries(statement.expenses.count) : null}
          />
          <CategoryCard
            lines={statement ? statement.expenses.categories : []}
            loading={loading}
            rows={5}
          />
        </React.Fragment>
      )}
    </div>
  );
}

/* ── summary ──────────────────────────────────────────────────────────── */

function SummaryCard({ rangeLabel, statement, anchorRef, onPickDate, children }) {
  return (
    <div
      style={{
        position: 'relative',
        // Above the sections below, so the date popover is not painted over.
        zIndex: 5,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusDialog,
      }}
    >
      <button
        ref={anchorRef}
        type="button"
        onClick={onPickDate}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '13px 18px',
          background: 'transparent',
          border: 'none',
          borderBottom: `1px solid ${T.border}`,
          font: 'inherit',
          fontSize: 13,
          color: T.ink,
          cursor: 'pointer',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ display: 'flex', color: T.inkFaint }}>
          <CalendarIcon size={15} />
        </span>
        <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>{rangeLabel}</span>
        <span style={{ display: 'flex', color: T.inkFaint }}>
          <ChevronIcon size={16} />
        </span>
      </button>

      {children}

      <div style={{ padding: '15px 18px 18px' }}>
        <span
          style={{ fontSize: 11, fontWeight: 600, color: T.inkFaint, letterSpacing: 0.3 }}
        >
          {COPY.netIncome}
        </span>

        <div style={{ paddingTop: 3 }}>
          {statement ? (
            <span
              style={{
                fontSize: 30,
                fontWeight: 700,
                letterSpacing: -0.9,
                // Colour flags the exception: a loss is red, a profit is plain
                // ink. Making profit green would spend colour on the expected
                // case — the phone makes the same choice.
                color: statement.netIncome < 0 ? T.danger : T.ink,
                ...NUMERIC,
              }}
            >
              {money(statement.netIncome)}
            </span>
          ) : (
            <Bone width={168} height={30} />
          )}
        </div>

        <div style={{ height: 1, background: T.border, margin: '16px 0 14px' }} />

        <div style={{ display: 'flex', gap: 24 }}>
          <Slot label={COPY.revenue} amount={statement ? statement.revenue.net : null} />
          <Slot label={COPY.expenses} amount={statement ? statement.expenses.total : null} />
        </div>
      </div>
    </div>
  );
}

function Slot({ label, amount }) {
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: T.inkSoft }}>{label}</span>
      {amount === null ? (
        <Bone width={86} height={17} />
      ) : (
        <span style={{ fontSize: 15, fontWeight: 700, color: T.ink, ...NUMERIC }}>
          {money(amount)}
        </span>
      )}
    </div>
  );
}

/* ── sections ─────────────────────────────────────────────────────────── */

function SectionHeader({ label, trailing }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '2px 2px 0' }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: T.ink }}>
        {label}
      </span>
      {/* Kept in place while the counts load so the layout does not jump. */}
      {trailing ? (
        <span
          style={{
            padding: '2px 11px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusControl,
            fontSize: 11.5,
            color: T.inkFaint,
          }}
        >
          {trailing}
        </span>
      ) : null}
    </div>
  );
}

function CategoryCard({ lines, loading, rows, footer }) {
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusDialog,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '4px 18px 6px' }}>
        {loading ? (
          <Skeleton rows={rows} />
        ) : (
          lines.map((line, index) => (
            <CategoryRow key={line.id || `${line.name}-${index}`} line={line} />
          ))
        )}
      </div>

      {footer ? (
        <React.Fragment>
          <div style={{ height: 1, background: T.border }} />
          <div style={{ padding: '11px 18px 12px' }}>{footer}</div>
        </React.Fragment>
      ) : null}
    </div>
  );
}

function CategoryRow({ line }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        padding: '11px 0',
      }}
    >
      <span
        title={line.name}
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          // Muted marks a catch-all bucket rather than a real category. It
          // flags a data gap without raising a warning about it.
          color: line.id ? T.ink : T.inkFaint,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {line.name}
      </span>
      <span
        style={{ minWidth: 52, textAlign: 'right', fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}
      >
        {percent(line.share)}
      </span>
      <span
        style={{
          minWidth: 92,
          textAlign: 'right',
          fontSize: 13,
          fontWeight: 700,
          color: T.ink,
          ...NUMERIC,
        }}
      >
        {money(line.amount)}
      </span>
    </div>
  );
}

/**
 * Gross and discounts, which reconcile the category list above to the net
 * figure in the summary card — so they belong to this section rather than to
 * a ledger block of their own.
 */
function RevenueFooter({ gross, discounts }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
        {COPY.gross} {money(gross)}
      </span>
      <span style={{ fontSize: 11.5, color: T.inkFaint }}>
        {COPY.discounts}{' '}
        <span style={{ fontWeight: 700, color: T.danger, ...NUMERIC }}>
          {/* Negated so the sign comes from the shared formatter rather than a
              minus glued on in front of it. */}
          {money(-Math.abs(discounts))}
        </span>
      </span>
    </div>
  );
}

/* ── loading, empty and failed ────────────────────────────────────────── */

function Skeleton({ rows }) {
  // Widths taper down the card so the block reads as a list of categories
  // rather than a solid slab.
  const widths = [128, 104, 116, 92, 110, 86];

  return (
    <React.Fragment>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', padding: '11px 0' }}>
          <Bone width={widths[index % widths.length]} height={13} />
          <span style={{ flex: 1 }} />
          <Bone width={34} height={11} />
          <span style={{ width: 12 }} />
          <Bone width={64} height={13} />
        </div>
      ))}
    </React.Fragment>
  );
}

function Bone({ width, height }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width,
        height,
        background: T.canvas,
        borderRadius: 6,
      }}
    />
  );
}

/**
 * One block for every state that replaces the breakdown: empty, failed, and
 * no permission. They differ only in wording and whether a retry is offered.
 */
function MessageBlock({ title, body, onRetry }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '56px 32px',
        textAlign: 'center',
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{title}</span>
      {body ? (
        <span style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.5 }}>{body}</span>
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 10,
            height: 38,
            padding: '0 18px',
            background: T.surface,
            border: `1px solid ${T.borderStrong}`,
            borderRadius: T.radiusPill,
            font: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            color: T.inkSoft,
            cursor: 'pointer',
          }}
        >
          {COPY.retry}
        </button>
      ) : null}
    </div>
  );
}
